import { useEffect, useState } from "react";

import { Button, Field, Stepper, TextInput, Toggle } from "../ui";
import { ChevronRightIcon, PencilIcon, PlusIcon, ReplayIcon, TrashIcon } from "../ui/icons";
import { MAX_ALARMS, alarmLabel, padAlarms } from "../../utilities/adherence";

/**
 * Edit form for one compartment. Three modes, unchanged from before the
 * revamp:
 *   soft  — edit the current pill's quantity, dose and alarms
 *   hard  — replace the pill, medicine name editable
 *   reset — remove the pill entirely
 * An empty compartment goes straight to "hard".
 *
 * Saves through handleBoxDataSave(boxNumber, medicineName, quantity, dose,
 * alarms), where alarms is a fixed 5-slot array: 0 is an unused slot and a
 * negative code is an alarm that is switched off.
 */

// Letters and combining marks in any script, plus digits, space, _ . -
const NAME_PATTERN = /^([\p{L}\p{M}0-9 _.-]+)$/u;

const NAME_ERRORS = {
  required: "Enter a medicine name.",
  length: "Keep the name to 30 characters or fewer.",
  character: "Use letters, numbers, spaces, and _ . - only.",
};

const alarmParts = (code) => ({
  hh: String(Math.floor(Math.abs(code) / 60) % 24).padStart(2, "0"),
  mm: String(Math.abs(code) % 60).padStart(2, "0"),
});

const BoxEditForm = ({ boxNumber, boxInfo, handleBoxDataSave, setIsEditModalOpen, isSaving }) => {
  const isEmptyBox = !boxInfo?.medicineName;

  const [mode, setMode] = useState(isEmptyBox ? "hard" : null);
  const [medicineName, setMedicineName] = useState(boxInfo?.medicineName || "");
  const [quantity, setQuantity] = useState(boxInfo?.quantity || 0);
  const [dose, setDose] = useState(boxInfo?.dose || 0);
  const [alarms, setAlarms] = useState(padAlarms(boxInfo?.alarms));
  const [errors, setErrors] = useState({});

  // Switching back to "soft" restores the saved values, as the old form did.
  useEffect(() => {
    if (mode === "soft") {
      setMedicineName(boxInfo?.medicineName || "");
      setQuantity(boxInfo?.quantity || 0);
      setDose(boxInfo?.dose || 0);
      setAlarms(padAlarms(boxInfo?.alarms));
      setErrors({});
    }
  }, [mode, boxInfo]);

  const activeAlarms = alarms.filter((code) => code !== 0);
  const canAddAlarm = activeAlarms.length < MAX_ALARMS;

  const setAlarmPart = (slot, part, raw) => {
    const limit = part === "hh" ? 23 : 59;
    const value = Math.max(0, Math.min(limit, Number(String(raw).replace(/\D/g, "") || 0)));
    setAlarms((prev) =>
      prev.map((code, index) => {
        if (index !== slot) return code;
        const { hh, mm } = alarmParts(code);
        const next = part === "hh" ? value * 60 + Number(mm) : Number(hh) * 60 + value;
        // 00:00 is stored as 1440 so the code stays truthy.
        const normalised = next === 0 ? 1440 : next;
        return code < 0 ? -normalised : normalised;
      }),
    );
  };

  const toggleAlarm = (slot) => setAlarms((prev) => prev.map((code, i) => (i === slot ? -code : code)));
  const removeAlarm = (slot) => setAlarms((prev) => prev.map((code, i) => (i === slot ? 0 : code)));
  const addAlarm = () => {
    const slot = alarms.findIndex((code) => code === 0);
    if (slot !== -1) setAlarms((prev) => prev.map((code, i) => (i === slot ? 480 : code)));
  };

  const validate = () => {
    const next = {};
    const name = medicineName.trim();
    if (!name) next.name = "required";
    else if (name.length > 30) next.name = "length";
    else if (!NAME_PATTERN.test(name)) next.name = "character";
    if (quantity < 0 || quantity > 999) next.quantity = "Quantity must be between 0 and 999.";
    if (dose < 0 || dose > 9) next.dose = "Dosage must be between 0 and 9.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = () => {
    if (validate()) handleBoxDataSave(boxNumber, medicineName.trim(), quantity, dose, alarms);
  };

  const close = () => setIsEditModalOpen(false);

  if (mode === null) {
    return (
      <div className="space-y-1">
        <ModeOption Icon={PencilIcon} onClick={() => setMode("soft")}>
          Edit details for current pill
        </ModeOption>
        <ModeOption Icon={ReplayIcon} onClick={() => setMode("hard")}>
          Remove current pill and add a new pill
        </ModeOption>
        <ModeOption Icon={TrashIcon} tone="danger" onClick={() => setMode("reset")}>
          Remove current pill
        </ModeOption>
        <div className="border-t border-gray-100 pt-4">
          <Button variant="ghost" size="sm" onClick={close}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  if (mode === "reset") {
    return (
      <>
        <p className="text-sm text-gray-600">
          <span className="font-semibold text-primary-900">{boxInfo.medicineName}</span> and its schedule will be removed from Box {boxNumber}.
        </p>
        <p className="mt-3 text-sm font-semibold text-primary-900">Are you sure you want to continue?</p>
        <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4">
          <Button variant="ghost" size="sm" onClick={() => setMode(null)}>
            Back
          </Button>
          <Button variant="danger" loading={isSaving} onClick={() => handleBoxDataSave(boxNumber, "", 0, 0, padAlarms([]))}>
            Remove pill
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <Field label="Medicine Name" error={errors.name && NAME_ERRORS[errors.name]} htmlFor="medicine-name">
        <TextInput
          id="medicine-name"
          type="text"
          value={medicineName}
          readOnly={mode === "soft"}
          maxLength={30}
          invalid={Boolean(errors.name)}
          placeholder="e.g. Metformin 500mg"
          onChange={(e) => setMedicineName(e.target.value)}
        />
      </Field>

      <Field label="Pill Quantity" error={errors.quantity}>
        <Stepper label="Pill quantity" value={quantity} max={999} onChange={setQuantity} />
      </Field>

      <Field label="Pill Dosage" error={errors.dose}>
        <Stepper label="Pill dosage" value={dose} max={9} onChange={setDose} />
      </Field>

      <p className="field-label">Alarms</p>
      {activeAlarms.length === 0 && <p className="mb-2 text-xs text-gray-500">No alarms set yet.</p>}
      <div className="mb-2 space-y-2">
        {alarms.map((code, slot) => {
          if (code === 0) return null;
          const { hh, mm } = alarmParts(code);
          const on = code > 0;
          return (
            <div key={slot} className="flex items-center gap-2">
              <input
                aria-label={`Alarm ${slot + 1} hour`}
                inputMode="numeric"
                value={hh}
                onChange={(e) => setAlarmPart(slot, "hh", e.target.value)}
                className="w-14 rounded-lg border border-gray-200 px-2 py-2 text-center text-sm focus:border-primary-500 focus:outline-none"
              />
              <span className="font-semibold text-gray-400">:</span>
              <input
                aria-label={`Alarm ${slot + 1} minute`}
                inputMode="numeric"
                value={mm}
                onChange={(e) => setAlarmPart(slot, "mm", e.target.value)}
                className="w-14 rounded-lg border border-gray-200 px-2 py-2 text-center text-sm focus:border-primary-500 focus:outline-none"
              />
              <span className="flex-1 truncate text-xs text-gray-500">{on ? alarmLabel(code) : "Off"}</span>
              <Toggle checked={on} onChange={() => toggleAlarm(slot)} label={`${on ? "Turn off" : "Turn on"} alarm ${slot + 1}`} />
              <button
                type="button"
                onClick={() => removeAlarm(slot)}
                aria-label={`Remove alarm ${slot + 1}`}
                className="flex-shrink-0 text-gray-400 transition hover:text-red-600"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
      <button
        type="button"
        onClick={addAlarm}
        disabled={!canAddAlarm}
        className="mb-6 flex items-center gap-1.5 text-xs font-semibold text-primary-600 disabled:text-gray-300"
      >
        <PlusIcon className="h-3.5 w-3.5" />
        {canAddAlarm ? "Add Alarm" : `Maximum ${MAX_ALARMS} alarms`}
      </button>

      <div className="flex items-center justify-between border-t border-gray-100 pt-4">
        <Button variant="ghost" size="sm" onClick={() => (isEmptyBox ? close() : setMode(null))}>
          {isEmptyBox ? "Cancel" : "Back"}
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={close}>
            Cancel
          </Button>
          <Button onClick={submit} loading={isSaving}>
            Save
          </Button>
        </div>
      </div>
    </>
  );
};

const ModeOption = ({ Icon, children, onClick, tone }) => (
  <button type="button" onClick={onClick} className="flex w-full items-center gap-3 rounded-lg px-1 py-3 text-left transition hover:bg-gray-50">
    <Icon className={`h-4 w-4 flex-shrink-0 ${tone === "danger" ? "text-red-500" : "text-primary-600"}`} />
    <span className={`flex-1 text-sm ${tone === "danger" ? "text-red-600" : "text-primary-900"}`}>{children}</span>
    <ChevronRightIcon className="h-4 w-4 text-gray-300" />
  </button>
);

export default BoxEditForm;
